export type Getter<T> = () => T;
export type ValueOrGetter<T> = T | (() => T);
export function isGetter<T>(x: ValueOrGetter<T>): x is Getter<T>
{
    return typeof x === "function";
}
export function evaluate<T>(x: ValueOrGetter<T>): T
{
    return isGetter(x) ? x() : x;
}
export class AugmentedFile
{
    /* implements File, Blob */
    constructor(private readonly file: File) { }

    get lastModified(): number
    {
        return this.file.lastModified;
    }

    get name(): string
    {
        return this.file.name;
    }

    get size(): number
    {
        return this.file.size;
    }

    get type(): string
    {
        return this.file.type;
    }

    slice(start?: number, end?: number, contentType?: string): Blob
    {
        return this.file.slice(start, end, contentType);
    }

    private _urlContents: string;

    private _resolvingUrlPromise: Promise<string>;

    readUrl(): Promise<string>
    {
        const self = this;
        if (this._urlContents)
        {
            return Promise.resolve(this._urlContents);
        }
        if (!this._resolvingUrlPromise)
        {
            this._resolvingUrlPromise = new Promise(getUrlContents);
        }
        return this._resolvingUrlPromise;
        function getUrlContents(
            resolveFn: (value: string | PromiseLike<string>) => void,
            rejectFn: (reason?: unknown) => void
        ): void
        {
            const reader = new FileReader();
            reader.onload = onLoad;
            reader.onerror = onError;
            // Todo - should this be abortable?
            // Todo - should this be done with a timeout that causes an abort and rejection?
            reader.readAsDataURL(self.file);
            function onLoad(): void
            {
                self._urlContents = reader.result as string;
                resolveFn(self._urlContents);
            }
            function onError(): void
            {
                rejectFn(reader.error);
            }
        }
    }

    private _base64Contents: string;

    private _resolvingBase64Promise: Promise<string>;

    readBase64(): Promise<string>
    {
        const self = this;
        if (this._base64Contents)
        {
            return Promise.resolve(this._base64Contents);
        }
        if (!this._resolvingBase64Promise)
        {
            this._resolvingBase64Promise = new Promise(getBase64Contents);
        }
        return this._resolvingBase64Promise;
        function getBase64Contents(
            resolveFn: (value: string | PromiseLike<string>) => void,
            rejectFn: (reason?: unknown) => void
        ): void
        {
            const reader = new FileReader();
            reader.onload = onLoad;
            reader.onerror = onError;
            // Todo - should this be abortable?
            // Todo - should this be done with a timeout that causes an abort and rejection?
            reader.readAsArrayBuffer(self.file);
            function onLoad(): void
            {
                const uInt8Array = new Uint8Array(reader.result as ArrayBuffer);
                let charCode = "";
                //Avoiding maximum stack depth
                for (let i = 0; i < uInt8Array.length; ++i)
                {
                    charCode += String.fromCharCode(uInt8Array[i]);
                }
                self._base64Contents = btoa(charCode);
                resolveFn(self._base64Contents);
            }
            function onError(): void
            {
                rejectFn(reader.error);
            }
        }
    }
}

/**
 *
 * @param bytes
 *
 * @see https://gist.github.com/lanqy/5193417
 */
export function bytesToSize(bytes: number, separator = " "): string
{
    /**
     * note: these are technically incorrect abbreviations now, they should be KiB, MiB, GiB, and TiB.
     * @see https://en.wikipedia.org/wiki/Kibibyte
     */
    const sizes = [
        "Bytes", "KB", "MB", "GB", "TB"
    ]; // GB and TB are unrealistic for web-based file uploads....
    if (!bytes) return "n/a";
    const index = Math.min(parseInt(Math.floor(Math.log(bytes) / Math.log(1024))
        .toString(), 10), sizes.length - 1);
    const units = sizes[index];
    const size = index > 0 ? (bytes / Math.pow(1024, index)).toFixed(2) : bytes;
    return `${size}${separator}${units}`;
}
